/**
 * Кадр веб-версии Fruit Slice с телефона на ТВ (GEN-18338):
 * `{type: 'FruitSliceWebData', gameState, mode, players, detectionProgress, hands, active}`.
 * Руки — доли кадра телефона 0…1 (y вниз), как у страницы в приложении.
 */
import type { FruitMode, Players, Point } from './fruitSliceRules.ts';

export const FRUIT_SLICE_WEB_FRAME_TYPE = 'FruitSliceWebData';

export type FruitSliceWebState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

type HandPair = readonly [Point | null, Point | null];

export interface FruitSliceWebFrame {
  readonly gameState: FruitSliceWebState;
  readonly mode: FruitMode;
  /** 1 — соло на всё поле, 2 — на двоих. */
  readonly players: Players;
  readonly detectionProgress: [number, number];
  readonly hands: readonly [HandPair, HandPair];
  readonly active: readonly [boolean, boolean];
  /** Время съёмки кадра на телефоне, мс (часы телефона, важны только разности): по нему считается скорость взмаха. */
  readonly t?: number;
}

const STATES: readonly string[] = ['PREPARING', 'STARTED', 'PAUSED', 'GAME_OVER'];
const MODES: readonly string[] = ['classic', 'arcade', 'zen'];

function point(value: unknown): Point | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function pair(value: unknown): HandPair {
  const list = Array.isArray(value) ? value : [];
  return [point(list[0]), point(list[1])];
}

function fraction(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/** Разбор кадра; `null` — это не кадр веб-версии (старые телефоны шлют прежние кадры Fruit Slice). */
export function parseFruitSliceWebFrame(raw: unknown): FruitSliceWebFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const frame = raw as Record<string, unknown>;
  if (frame.type !== FRUIT_SLICE_WEB_FRAME_TYPE) return null;
  const gameState = STATES.includes(frame.gameState as string) ? frame.gameState as FruitSliceWebState : 'PREPARING';
  const mode = MODES.includes(frame.mode as string) ? frame.mode as FruitMode : 'classic';
  const progress = Array.isArray(frame.detectionProgress) ? frame.detectionProgress : [];
  const hands = Array.isArray(frame.hands) ? frame.hands : [];
  const active = Array.isArray(frame.active) ? frame.active : [];
  return {
    gameState,
    mode,
    players: frame.players === 1 ? 1 : 2,
    detectionProgress: [fraction(progress[0]), fraction(progress[1])],
    hands: [pair(hands[0]), pair(hands[1])],
    active: [active[0] !== false, active[1] !== false],
    ...(typeof frame.t === 'number' && Number.isFinite(frame.t) ? { t: frame.t } : {}),
  };
}
